import usePageSEO from '../hooks/usePageSEO';
import { FAQSchema, ProductSchema } from '../components/JsonLd';

import { motion } from 'framer-motion';
import Footer from '../components/Footer';
import { 
  Shield, 
  Lock, 
  Eye, 
  EyeOff,
  Database,
  Key,
  UserCheck,
  RefreshCw,
  Trash2,
  CheckCircle2,
  ArrowRight,
  Mail,
  Fingerprint,
  Server,
  FileCheck,
  ShieldCheck,
  Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_64eb10e1-fdde-4977-afc2-4761fbf5691b/artifacts/5pkoywp1_Plouton_Logo%20%281%29.png";
const CAL_LINK = "https://cal.com/muhammad-furqan-karim-kidwai-uvsohz/30min";

const faqs = [
  {
    question: 'How is my data stored?',
    answer: 'Your data is stored in encrypted databases using AES-256 encryption. Data in transit between your systems and Plouton uses TLS 1.3 encryption. We use industry-leading cloud infrastructure with automatic backups and disaster recovery.',
  },
  {
    question: 'Who can access my data?',
    answer: 'Only authorized users in your organization can access your Plouton account. Our engineering team follows strict least-privilege principles: no employee has unnecessary access to customer data. Any access for support purposes requires your explicit permission and is fully logged.',
  },
  {
    question: 'What happens if I disconnect Plouton?',
    answer: 'You maintain complete control. You can disconnect Plouton from your financial systems at any time. When you disconnect or delete your account, we permanently delete all your financial data from our systems within 30 days.',
  },
  {
    question: 'Do you sell or share my data?',
    answer: 'Never. We do not sell, rent, or share your financial data with anyone. Your data is yours and yours alone. We don\'t use it for advertising, we don\'t share it with partners, and we don\'t use it to train AI models.',
  },
  {
    question: 'How do you handle security incidents?',
    answer: 'We have a comprehensive incident response plan. In the unlikely event of a security incident, we\'ll notify affected customers within 72 hours and provide full transparency about what happened, what data was affected, and what steps we\'re taking.',
  },
];

export const SecurityPage = () => {
  usePageSEO({
    title: 'Secure AI Agents for Finance Automation | Plouton AI',
    description: 'Keep your data safe with enterprise-grade security. Our AI agents for financial automation offer full auditability to protect your daily workflows. Get started!',
    path: '/security'
  });

  return (
    <div className="min-h-screen bg-[#0a0a0f] pt-[108px] overflow-hidden">
      <FAQSchema faqs={faqs} />
      <ProductSchema
        name="Plouton AI Security"
        description="Enterprise-grade security with SOC 2 Type II certification, AES-256 encryption, and GDPR compliance for financial data."
        url="https://plouton.co/security"
      />
      {/* Hero Section */}
      <section className="relative py-24 sm:py-32">
        {/* Animated background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-orange-500/10 rounded-full blur-[100px]" />
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMDIwMzAiIGZpbGwtb3BhY2l0eT0iMC40Ij48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnY0em0wLTZ2LTRoLTJ2NGgyek0zMCAzNHYtNGgtMnY0aDJ6bTAtNnYtNGgtMnY0aDJ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-20" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Badge */}
            <motion.div 
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full mb-8"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <ShieldCheck className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-medium text-blue-400">SOC 2 Type II Certified</span>
            </motion.div>

            <h1 
              className="font-heading text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight text-white mb-6"
              data-testid="security-headline"
            >
              Security that
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-blue-500 to-orange-400">
                never sleeps.
              </span>
            </h1>
            
            <p className="text-xl text-slate-400 leading-relaxed max-w-2xl mx-auto mb-12">
              Enterprise-grade protection for your most sensitive financial data. 
              Built from day one to exceed the standards you require.
            </p>

            {/* Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
              {[
                { value: 'SOC 2', label: 'Type II Certified' },
                { value: 'AES-256', label: 'Encryption' },
                { value: '99.99%', label: 'Uptime SLA' },
                { value: 'GDPR', label: 'Compliant' },
              ].map((stat, idx) => (
                <motion.div
                  key={stat.label}
                  className="relative group"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 + idx * 0.1 }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-orange-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-white/20 transition-colors">
                    <div className="text-2xl font-bold text-white font-heading">{stat.value}</div>
                    <div className="text-sm text-slate-500">{stat.label}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Bento Grid Section */}
      <section className="py-24 relative">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              How we protect your data
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Multiple layers of security, working together seamlessly.
            </p>
          </motion.div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Large Card - Encryption */}
            <motion.div
              className="lg:col-span-2 relative group"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-transparent rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              <div className="relative h-full bg-gradient-to-br from-slate-900 to-slate-900/50 border border-slate-800 rounded-3xl p-8 lg:p-10 overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                <div className="relative">
                  <div className="w-14 h-14 rounded-2xl bg-blue-600/20 flex items-center justify-center mb-6">
                    <Lock className="w-7 h-7 text-blue-400" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-white mb-3">
                    End-to-end encryption
                  </h3>
                  <p className="text-slate-400 mb-6 max-w-lg">
                    Your data is encrypted at rest with AES-256 and in transit with TLS 1.3. 
                    We use the same encryption standards trusted by banks and governments.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {['AES-256', 'TLS 1.3', 'Zero-knowledge'].map((tag) => (
                      <span key={tag} className="px-3 py-1 bg-blue-500/10 text-blue-400 text-sm rounded-full border border-blue-500/20">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* OAuth Card */}
            <motion.div
              className="relative group"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div className="h-full bg-gradient-to-br from-slate-900 to-slate-900/50 border border-slate-800 rounded-3xl p-8 hover:border-slate-700 transition-colors">
                <div className="w-14 h-14 rounded-2xl bg-orange-500/20 flex items-center justify-center mb-6">
                  <Key className="w-7 h-7 text-orange-400" />
                </div>
                <h3 className="font-heading text-xl font-bold text-white mb-3">
                  Secure OAuth 2.0
                </h3>
                <p className="text-slate-400 text-sm">
                  Connect to your financial systems securely. No passwords stored, no credentials cached.
                </p>
              </div>
            </motion.div>

            {/* Read-only Card */}
            <motion.div
              className="relative group"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="h-full bg-gradient-to-br from-slate-900 to-slate-900/50 border border-slate-800 rounded-3xl p-8 hover:border-slate-700 transition-colors">
                <div className="w-14 h-14 rounded-2xl bg-green-500/20 flex items-center justify-center mb-6">
                  <Eye className="w-7 h-7 text-green-400" />
                </div>
                <h3 className="font-heading text-xl font-bold text-white mb-3">
                  Read-only access
                </h3>
                <p className="text-slate-400 text-sm">
                  We only request read permissions. We can't modify, delete, or transfer your data.
                </p>
              </div>
            </motion.div>

            {/* RBAC Card */}
            <motion.div
              className="relative group"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="h-full bg-gradient-to-br from-slate-900 to-slate-900/50 border border-slate-800 rounded-3xl p-8 hover:border-slate-700 transition-colors">
                <div className="w-14 h-14 rounded-2xl bg-purple-500/20 flex items-center justify-center mb-6">
                  <UserCheck className="w-7 h-7 text-purple-400" />
                </div>
                <h3 className="font-heading text-xl font-bold text-white mb-3">
                  Role-based access
                </h3>
                <p className="text-slate-400 text-sm">
                  RBAC ensures only authorized users access your financial data. Full control, always.
                </p>
              </div>
            </motion.div>

            {/* Audit Trail Card - Wide */}
            <motion.div
              className="lg:col-span-2 relative group"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="h-full bg-gradient-to-br from-slate-900 to-slate-900/50 border border-slate-800 rounded-3xl p-8 lg:p-10 hover:border-slate-700 transition-colors">
                <div className="flex flex-col lg:flex-row lg:items-center gap-8">
                  <div className="flex-1">
                    <div className="w-14 h-14 rounded-2xl bg-cyan-500/20 flex items-center justify-center mb-6">
                      <FileCheck className="w-7 h-7 text-cyan-400" />
                    </div>
                    <h3 className="font-heading text-2xl font-bold text-white mb-3">
                      Complete audit trails
                    </h3>
                    <p className="text-slate-400">
                      Every action is logged with screen recordings. Human review checkpoints at configurable stages. 
                      Regulator-ready documentation generated automatically.
                    </p>
                  </div>
                  <div className="flex-shrink-0 grid grid-cols-3 gap-4">
                    {[
                      { icon: Zap, label: 'Real-time' },
                      { icon: Eye, label: 'Observable' },
                      { icon: Shield, label: 'Compliant' },
                    ].map((item) => (
                      <div key={item.label} className="text-center">
                        <div className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center mx-auto mb-2">
                          <item.icon className="w-5 h-5 text-slate-400" />
                        </div>
                        <span className="text-xs text-slate-500">{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Privacy Promise Section */}
      <section className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-950/20 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-orange-500/10 border border-orange-500/20 mb-8">
                <EyeOff className="w-10 h-10 text-orange-400" />
              </div>
              <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6">
                Your data is <span className="text-orange-400">never</span> used to train AI models.
              </h2>
              <p className="text-xl text-slate-400 max-w-2xl mx-auto">
                Your proprietary financial information stays completely private. 
                It's never shared, never used for model training, and never accessed by anyone outside your organization.
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-3 gap-6">
              {[
                { 
                  icon: Shield, 
                  title: 'Complete privacy', 
                  desc: 'Your data is never used to improve our AI or shared with third parties.' 
                },
                { 
                  icon: Database, 
                  title: 'No data leakage', 
                  desc: 'Your sensitive info will never appear in responses to other customers.' 
                },
                { 
                  icon: Trash2, 
                  title: 'Permanent deletion', 
                  desc: 'Delete your account and your data is permanently removed within 30 days.' 
                },
              ].map((item, index) => (
                <motion.div
                  key={item.title}
                  className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-6 h-6 text-orange-400" />
                  </div>
                  <h3 className="font-heading font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-400">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading text-3xl font-bold text-white mb-4">
              Security FAQ
            </h2>
          </motion.div>

          <Accordion type="single" collapsible className="space-y-4" data-testid="security-faq">
            {faqs.map((faq) => (
              <AccordionItem 
                key={faq.question} 
                value={faq.question}
                className="bg-slate-900/50 border border-slate-800 rounded-2xl px-6 data-[state=open]:border-slate-700 transition-colors"
              >
                <AccordionTrigger className="text-left font-semibold text-white hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Have security questions?
          </h2>
          <p className="text-lg text-slate-600 mb-8 max-w-xl mx-auto">
            Our security team is here to help. Get detailed documentation for your compliance team.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="mailto:security@plouton.co">
              <Button 
                variant="outline"
                className="border-slate-300 text-slate-700 hover:bg-slate-50 hover:text-slate-900 px-6 py-5"
              >
                <Mail className="w-5 h-5 mr-2" />
                security@plouton.co
              </Button>
            </a>
            <a href={CAL_LINK} target="_blank" rel="noopener noreferrer">
              <Button 
                className="bg-blue-600 text-white hover:bg-blue-700 px-6 py-5 font-semibold"
                data-testid="security-book-demo"
              >
                Book a Demo
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SecurityPage;
